// src/components/admin/CategoryImportDialog.jsx
import React, { useState } from 'react';
import { X, Upload, Download, CheckCircle2, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useApp } from '../../context/AppContext';
import { categoryApi } from '../../api';

// Excel解析放在浏览器里做，不用把文件传到服务器再解析——
// 好处是解析完可以先给用户看预览表格，确认没填错再提交，
// 提交给后端的是一份已经整理好的干净数据（不是原始文件）
const CategoryImportDialog = ({ onClose, onImported }) => {
  const { language } = useApp();
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]); // [{ parentName, childName }]
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { created, skipped, failed }

  const T = {
    title: language === 'en' ? 'Batch Import Categories' : language === 'km' ? 'នាំចូលប្រភេទច្រើន' : '批量导入分类',
    downloadTemplate: language === 'en' ? 'Download Template' : language === 'km' ? 'ទាញយកគំរូ' : '下载模板',
    choosePlaceholder: language === 'en' ? 'Click to choose an Excel file (.xlsx)' : language === 'km' ? 'ចុចដើម្បីជ្រើសរើសឯកសារ Excel (.xlsx)' : '点击选择Excel文件（.xlsx）',
    previewLabel: (n) => language === 'en' ? `Preview (${n} rows) — check before confirming`
      : language === 'km' ? `មើលជាមុន (${n} ជួរ) — សូមពិនិត្យមុនបញ្ជាក់`
      : `预览（共 ${n} 行，确认无误再点下方"确认导入"）`,
    colParent: language === 'en' ? 'Category' : language === 'km' ? 'ប្រភេទធំ' : '大类',
    colParentEn: language === 'en' ? 'Category (EN)' : language === 'km' ? 'ប្រភេទធំ (EN)' : '大类(英文)',
    colParentKm: language === 'en' ? 'Category (KM)' : language === 'km' ? 'ប្រភេទធំ (KM)' : '大类(高棉语)',
    colParentMs: language === 'en' ? 'Category (MS)' : language === 'km' ? 'ប្រភេទធំ (MS)' : '大类(马来语)',
    colParentZhTw: language === 'en' ? 'Category (ZH-TW)' : language === 'km' ? 'ប្រភេទធំ (ZH-TW)' : '大类(繁体中文)',
    colChild: language === 'en' ? 'Subcategory' : language === 'km' ? 'ប្រភេទរង' : '子类',
    colChildEn: language === 'en' ? 'Subcategory (EN)' : language === 'km' ? 'ប្រភេទរង (EN)' : '子类(英文)',
    colChildKm: language === 'en' ? 'Subcategory (KM)' : language === 'km' ? 'ប្រភេទរង (KM)' : '子类(高棉语)',
    colChildMs: language === 'en' ? 'Subcategory (MS)' : language === 'km' ? 'ប្រភេទរង (MS)' : '子类(马来语)',
    colChildZhTw: language === 'en' ? 'Subcategory (ZH-TW)' : language === 'km' ? 'ប្រភេទរង (ZH-TW)' : '子类(繁体中文)',
    none: language === 'en' ? '(none)' : language === 'km' ? '(គ្មាន)' : '（无）',
    statCreated: language === 'en' ? 'Created' : language === 'km' ? 'បានបង្កើត' : '新建',
    statSkipped: language === 'en' ? 'Already existed' : language === 'km' ? 'មានរួចហើយ' : '已存在跳过',
    statFailed: language === 'en' ? 'Failed' : language === 'km' ? 'បរាជ័យ' : '失败',
    failedDetail: language === 'en' ? 'Failure details' : language === 'km' ? 'ព័ត៌មានលម្អិតបញ្ហា' : '失败详情',
    createdList: language === 'en' ? 'Newly created' : language === 'km' ? 'ប្រភេទដែលបានបង្កើតថ្មី' : '新建的分类',
    cancel: language === 'en' ? 'Cancel' : language === 'km' ? 'បោះបង់' : '取消',
    confirmImport: (n) => language === 'en' ? `Confirm Import (${n})` : language === 'km' ? `បញ្ជាក់នាំចូល (${n})` : `确认导入（${n}条）`,
    importing: language === 'en' ? 'Importing...' : language === 'km' ? 'កំពុងនាំចូល...' : '导入中...',
    done: language === 'en' ? 'Done' : language === 'km' ? 'រួចរាល់' : '完成',
    noDataError: language === 'en'
      ? 'No importable data found — check that example rows were removed and real data starts from row 3.'
      : language === 'km'
      ? 'រកមិនឃើញទិន្នន័យសម្រាប់នាំចូល — សូមពិនិត្យថាបានលុបជួរឧទាហរណ៍ ហើយទិន្នន័យពិតចាប់ផ្តើមពីជួរទី៣។'
      : '没有读到可导入的数据，检查一下是不是把示例行也删了、数据没有从第3行开始填。',
    parseError: (msg) => language === 'en' ? `File parsing failed, make sure it's a standard .xlsx file: ${msg}`
      : language === 'km' ? `ការញែកឯកសារបរាជ័យ សូមប្រាកដថាជាឯកសារ .xlsx ស្តង់ដារ៖ ${msg}`
      : '文件解析失败，确认一下是不是标准的.xlsx文件：' + msg,
  };

  // 生成模板文件——表头 + 一行示例，列顺序和上面的解析逻辑完全对应
  const downloadTemplate = () => {
    const headers = [
      '大类(中)', '大类(英)', '大类(高棉语)', '大类(马来语)', '大类(繁体中文)',
      '子类(中)', '子类(英)', '子类(高棉语)', '子类(马来语)', '子类(繁体中文)'
    ];
    const example = [
      '示例：饮料', 'Beverages', 'ភេសជ្ជៈ', 'Minuman', '飲料',
      '碳酸饮料', 'Carbonated Drinks', 'ភេសជ្ជៈមានហ្គាស', 'Minuman Berkarbonat', '碳酸飲料'
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'categories');
    XLSX.writeFile(wb, '分类批量导入模板.xlsx');
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    setResult(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        // header:1 => 拿原始二维数组，自己按列位置取值
        const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        // 第1行表头，第2行示例行，第3行开始才是真实数据（和商品导入脚本用同一套约定）
        const dataRows = raw.slice(2)
          .filter(row => row.some(cell => String(cell).trim() !== ''))
          .map(row => ({
            parentName: String(row[0] || '').trim(),
            parentNameEn: String(row[1] || '').trim(),
            parentNameKm: String(row[2] || '').trim(),
            parentNameMs: String(row[3] || '').trim(),
            parentNameZhTw: String(row[4] || '').trim(),
            childName: String(row[5] || '').trim(),
            childNameEn: String(row[6] || '').trim(),
            childNameKm: String(row[7] || '').trim(),
            childNameMs: String(row[8] || '').trim(),
            childNameZhTw: String(row[9] || '').trim(),
          }));
        if (dataRows.length === 0) {
          setParseError(T.noDataError);
          return;
        }
        setRows(dataRows);
      } catch (err) {
        setParseError(T.parseError(err.message));
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSubmit = async () => {
    if (rows.length === 0) return;
    setSubmitting(true);
    try {
      const res = await categoryApi.bulkImport({ items: rows });
      if (res.success) {
        setResult(res.data);
        onImported(); // 通知父页面刷新分类列表
      } else {
        setParseError(res.message || T.parseError(''));
      }
    } catch (err) {
      setParseError(err.message || T.parseError(''));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="border-b p-6 flex justify-between items-center flex-shrink-0">
          <h3 className="text-xl font-bold">{T.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {!result && (
            <button onClick={downloadTemplate}
              className="w-full flex items-center justify-center gap-2 border border-blue-200 bg-blue-50 text-blue-600 rounded-lg py-2.5 text-sm font-medium hover:bg-blue-100 transition-colors">
              <Download className="w-4 h-4" />
              {T.downloadTemplate}
            </button>
          )}

          {/* 选择文件 */}
          {!result && (
            <div>
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {fileName || T.choosePlaceholder}
                </span>
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFile} />
              </label>
              {parseError && (
                <p className="text-sm text-red-600 mt-2">{parseError}</p>
              )}
            </div>
          )}

          {/* 预览表格：提交前先给用户看一眼，确认数据对不对 */}
          {rows.length > 0 && !result && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                {T.previewLabel(rows.length)}
              </p>
              <div className="border rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-sm min-w-[1080px]">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{T.colParent}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{T.colParentEn}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{T.colParentKm}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{T.colParentMs}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{T.colParentZhTw}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{T.colChild}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{T.colChildEn}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{T.colChildKm}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{T.colChildMs}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{T.colChildZhTw}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{r.parentName}</td>
                        <td className="px-3 py-2 text-gray-500">{r.parentNameEn || <span className="text-gray-300">{T.none}</span>}</td>
                        <td className="px-3 py-2 text-gray-500">{r.parentNameKm || <span className="text-gray-300">{T.none}</span>}</td>
                        <td className="px-3 py-2 text-gray-500">{r.parentNameMs || <span className="text-gray-300">{T.none}</span>}</td>
                        <td className="px-3 py-2 text-gray-500">{r.parentNameZhTw || <span className="text-gray-300">{T.none}</span>}</td>
                        <td className="px-3 py-2 text-gray-500">{r.childName || <span className="text-gray-300">{T.none}</span>}</td>
                        <td className="px-3 py-2 text-gray-500">{r.childNameEn || <span className="text-gray-300">{T.none}</span>}</td>
                        <td className="px-3 py-2 text-gray-500">{r.childNameKm || <span className="text-gray-300">{T.none}</span>}</td>
                        <td className="px-3 py-2 text-gray-500">{r.childNameMs || <span className="text-gray-300">{T.none}</span>}</td>
                        <td className="px-3 py-2 text-gray-500">{r.childNameZhTw || <span className="text-gray-300">{T.none}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 导入结果 */}
          {result && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{result.created.length}</p>
                  <p className="text-xs text-green-700 mt-1">{T.statCreated}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-500">{result.skipped.length}</p>
                  <p className="text-xs text-gray-600 mt-1">{T.statSkipped}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{result.failed.length}</p>
                  <p className="text-xs text-red-700 mt-1">{T.statFailed}</p>
                </div>
              </div>

              {result.failed.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />{T.failedDetail}
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {result.failed.map((f, i) => (
                      <div key={i} className="text-xs bg-red-50 rounded px-3 py-2 text-red-700">
                        「{f.parentName}{f.childName ? ' / ' + f.childName : ''}」：{f.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.created.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-green-700 mb-2 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />{T.createdList}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {result.created.map((c, i) => (
                      <span key={i} className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full">
                        {c.level === 2 ? `${c.parent} / ${c.name}` : c.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t p-6 flex justify-end space-x-3 flex-shrink-0">
          {!result ? (
            <>
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                {T.cancel}
              </button>
              <button
                onClick={handleSubmit}
                disabled={rows.length === 0 || submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? T.importing : T.confirmImport(rows.length)}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              {T.done}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryImportDialog;